import {ScrollArea} from '@/base/ui/scrollArea'
import {
  Sidebar as Shadbar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/base/ui/sidebar'
import {cn} from '@/lib/utils'
import React, {useRef} from 'react'
import {WuMenuIcon} from '../menuIcon'

export type IWuSidebarProps = {
  children: React.ReactNode
  Sidebar: React.ReactNode
  className?: string
  style?: React.CSSProperties
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export const WuSidebar: React.FC<IWuSidebarProps> = ({
  children,
  Sidebar,
  className,
  defaultOpen = true,
  open,
  onOpenChange,
  style,
}) => (
  <SidebarProvider
    defaultOpen={defaultOpen}
    open={open}
    onOpenChange={onOpenChange}
  >
    <Shadbar
      variant="sidebar"
      collapsible="icon"
      className={cn(
        'top-12 h-[calc(100%-48px)] shadow-sidebar border-none',
        className,
      )}
      style={style}
      role="complementary"
    >
      <SidebarHeader className="py-2">
        <WuSidebarTrigger />
      </SidebarHeader>
      {Sidebar}
    </Shadbar>
    <SidebarInset>{children}</SidebarInset>
  </SidebarProvider>
)

export const WuSidebarTrigger: React.FC<
  React.ComponentProps<typeof SidebarTrigger>
> = props => {
  const {state, isMobile, openMobile} = useSidebar()
  return (
    <SidebarTrigger {...props}>
      <WuMenuIcon showMenu={isMobile ? openMobile : state === 'expanded'} />
    </SidebarTrigger>
  )
}

export const WuSidebarContent: React.FC<
  React.ComponentProps<typeof SidebarContent>
> = ({className, children, ...props}) => {
  const topHrRef = useRef<HTMLHRElement>(null)
  const bottomHrRef = useRef<HTMLHRElement>(null)

  const handleScroll = ({
    currentTarget,
  }: React.UIEvent<HTMLDivElement>): void => {
    const {scrollTop, clientHeight, scrollHeight} = currentTarget

    if (topHrRef.current) {
      topHrRef.current.classList.toggle('border-[#1b338014]', scrollTop !== 0)
    }

    if (bottomHrRef.current) {
      bottomHrRef.current.classList.toggle(
        'border-[#1b338014]',
        scrollTop + clientHeight !== scrollHeight,
      )
    }
  }

  const hrClassName = 'w-[calc(100%-16px)] mx-auto border-transparent'

  return (
    <SidebarContent
      {...props}
      className={cn('overflow-hidden gap-0', className)}
    >
      <hr className={hrClassName} ref={topHrRef} />
      <ScrollArea
        onScroll={handleScroll}
        className="gap-2"
        thumbColor="#b9c3dc"
        railColor="#00000000"
        data-testid="scroll-area"
      >
        {children}
      </ScrollArea>
      <hr className={hrClassName} ref={bottomHrRef} />
    </SidebarContent>
  )
}

export const WuSidebarFooter: React.FC<
  React.ComponentProps<typeof SidebarFooter>
> = props => <SidebarFooter {...props}>{props.children}</SidebarFooter>

export type IWuSidebarGroupProps = React.ComponentProps<typeof SidebarGroup> & {
  label: string
}

export const WuSidebarGroup: React.FC<IWuSidebarGroupProps> = ({
  label,
  children,
  ...props
}) => (
  <SidebarGroup {...props} className="p-0">
    <div className="h-8">
      <SidebarGroupLabel className="items-end px-4 text-xs font-normal leading-4 text-blue-q">
        {label}
      </SidebarGroupLabel>
    </div>
    <SidebarGroupContent>
      <SidebarMenu>{children}</SidebarMenu>
    </SidebarGroupContent>
  </SidebarGroup>
)

export const WuSidebarMenu: React.FC<
  React.ComponentProps<typeof SidebarMenu>
> = props => <SidebarMenu {...props} />

export type IWuSidebarItemProps = React.ComponentProps<
  typeof SidebarMenuItem
> & {
  Icon: React.ReactNode
  isActive?: boolean
  children?: React.ReactElement<React.AnchorHTMLAttributes<HTMLAnchorElement>>
}

export const WuSidebarItem: React.FC<IWuSidebarItemProps> = ({
  Icon,
  children,
  className,
  isActive,
  ...rest
}) => {
  const {state} = useSidebar()
  const [tooltip, setTooltip] = React.useState<string | undefined>()

  const modifiedChild =
    children &&
    React.cloneElement(children, {
      children: (
        <div className="flex items-center gap-2 overflow-hidden text-base truncate text-ellipsis">
          {Icon}

          <span
            className={cn(
              'text-sm leading-4 transition-opacity duration-150',
              state === 'collapsed' && 'opacity-0',
            )}
          >
            {children.props.children}
          </span>
        </div>
      ),
    })

  React.useEffect(() => {
    setTooltip(String(children?.props.children).replace(' ,', ' '))
  }, [])
  return (
    <SidebarMenuItem {...rest} className="flex items-center gap-2 px-2">
      <SidebarMenuButton
        className={cn(
          'flex items-center text-blue-q hover:bg-blue-sidebarHover cursor-pointer h-8 rounded-sm',
          className,
          isActive &&
            'bg-white shadow-[4px_4px_16px_0_#1B338014] font-medium text-blue-p hover:bg-white cursor-default',
        )}
        isActive={isActive}
        tooltip={tooltip}
        asChild
      >
        {modifiedChild}
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
